import React from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import {
  addAppCalendarDays,
  formatAppDate,
  formatAppIntlLabel,
  getAppWallDateParts,
  getAppWeekStartIso,
  parseDbDateTime,
  toAppDateKey,
} from "../../utils/appDateTime";

function isoFromDate(currentDate) {
  if (!currentDate) return toAppDateKey(new Date());
  if (currentDate instanceof Date) return toAppDateKey(currentDate);
  const parsed = parseDbDateTime(currentDate);
  return parsed ? toAppDateKey(parsed) : String(currentDate).slice(0, 10);
}

function dateFromIso(iso) {
  return parseDbDateTime(`${iso}T12:00:00Z`) || new Date();
}

const DateNavigator = ({ activeTab, currentDate, setCurrentDate }) => {
  const handlePrev = () => {
    const iso = isoFromDate(currentDate);
    if (activeTab === "DAY" || activeTab === "TODAY") {
      setCurrentDate(dateFromIso(addAppCalendarDays(iso, -1)));
      return;
    }
    if (activeTab === "WEEK") {
      setCurrentDate(dateFromIso(addAppCalendarDays(iso, -7)));
      return;
    }
    if (activeTab === "MONTH") {
      const parts = getAppWallDateParts(parseDbDateTime(`${iso}T12:00:00Z`));
      if (!parts) return;
      const month = parts.month === 1 ? 12 : parts.month - 1;
      const year = parts.month === 1 ? parts.year - 1 : parts.year;
      setCurrentDate(dateFromIso(`${year}-${String(month).padStart(2, "0")}-01`));
    }
  };

  const handleNext = () => {
    const iso = isoFromDate(currentDate);
    if (activeTab === "DAY" || activeTab === "TODAY") {
      setCurrentDate(dateFromIso(addAppCalendarDays(iso, 1)));
      return;
    }
    if (activeTab === "WEEK") {
      setCurrentDate(dateFromIso(addAppCalendarDays(iso, 7)));
      return;
    }
    if (activeTab === "MONTH") {
      const parts = getAppWallDateParts(parseDbDateTime(`${iso}T12:00:00Z`));
      if (!parts) return;
      const month = parts.month === 12 ? 1 : parts.month + 1;
      const year = parts.month === 12 ? parts.year + 1 : parts.year;
      setCurrentDate(dateFromIso(`${year}-${String(month).padStart(2, "0")}-01`));
    }
  };

  const formatDate = () => {
    const iso = isoFromDate(currentDate);
    const anchor = parseDbDateTime(`${iso}T12:00:00Z`);
    if (!anchor) return iso;

    if (activeTab === "TODAY" || activeTab === "DAY") {
      return formatAppDate(anchor, {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    }
    if (activeTab === "WEEK") {
      const weekStart = getAppWeekStartIso(iso);
      const weekEnd = addAppCalendarDays(weekStart, 6);
      const startAnchor = parseDbDateTime(`${weekStart}T12:00:00Z`);
      const endAnchor = parseDbDateTime(`${weekEnd}T12:00:00Z`);
      const startFormatted = formatAppIntlLabel(startAnchor, {
        month: "short",
        day: "numeric",
      });
      const endFormatted = formatAppIntlLabel(endAnchor, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      return `${startFormatted} – ${endFormatted}`;
    }
    if (activeTab === "MONTH") {
      return formatAppIntlLabel(anchor, { month: "long", year: "numeric" });
    }
    return formatAppDate(anchor);
  };

  return (
    <div className="flex items-center justify-left space-x-2 my-3">
      {activeTab !== "TODAY" && (
        <button onClick={handlePrev} className="p-1 hover:bg-gray-100 rounded-full">
          <FaChevronLeft className="text-gray-600" />
        </button>
      )}

      <div className="text-2xl font-semibold text-[#3A4149]">{formatDate()}</div>

      {activeTab !== "TODAY" && (
        <button onClick={handleNext} className="p-1 hover:bg-gray-100 rounded-full">
          <FaChevronRight className="text-gray-600" />
        </button>
      )}
    </div>
  );
};

export default DateNavigator;
